<script setup lang="ts">
/**
 * The pin beside a field label: fixes the field's value for the records that
 * follow (BLP/nx#3198, fast posting capture — XE's „fixieren“ check box next to
 * a field). Empty pin = not fixed, filled pin = fixed; the tooltip says what a
 * click does. The app decides what „fixed“ means; the kit only shows and
 * toggles it (`v-model:pinned`). The kit's words are the general ones
 * („Fixieren“ / „Fixierung lösen“); `pinDetail` adds what fixing does on the
 * app's screen („Fixieren: <pinDetail>“).
 *
 * Out of the Tab order (`tabindex="-1"`): the fields are typed one after the
 * other, the pin is clicked, or toggled by a key the app binds. `aria-pressed`
 * tells a screen reader its state; the accessible name is the tooltip text.
 *
 * Place it BESIDE the `<label>` (NxInputContainerItem's `#label-suffix`,
 * NxLabel's `#info`), never inside: a control inside a label forwards its
 * clicks to the input.
 */
import { PinIcon } from '@blpde/nx-icons';
import { computed, inject } from 'vue';
import { i18nInjectionKey } from '../internal';
import { NxTooltip } from '../Tooltip';

const pinned = defineModel<boolean>('pinned', { default: false });

const props = withDefaults(
  defineProps<{
    /** Locked: shows the state, takes no click. */
    disabled?: boolean;
    /**
     * What fixing does here, appended to the tooltip of an unpinned field:
     * „Fixieren: <pinDetail>“. Without it the tooltip says „Fixieren“ only.
     */
    pinDetail?: string;
  }>(),
  { disabled: false, pinDetail: '' }
);

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string): string {
  const full = `common.pin_toggle.${key}`;
  const text = i18n?.t(full);
  return text && text !== full ? text : fallback;
}

const hint = computed(() => {
  if (pinned.value) return tr('unpin', 'Fixierung lösen');
  const pin = tr('pin', 'Fixieren');
  return props.pinDetail ? `${pin}: ${props.pinDetail}` : pin;
});

function toggle(): void {
  if (!props.disabled) pinned.value = !pinned.value;
}
</script>

<template>
  <NxTooltip :text="hint">
    <button
      class="nx-pin-toggle"
      :class="{ 'is-pinned': pinned }"
      type="button"
      tabindex="-1"
      :aria-label="hint"
      :aria-pressed="pinned"
      :disabled="props.disabled"
      data-testid="nx-pin-toggle"
      @click="toggle"
    >
      <PinIcon class="nx-pin-toggle__icon" :filled="pinned" aria-hidden="true" />
    </button>
  </NxTooltip>
</template>

<style scoped>
/* The size and quiet look of NxInfoHint, its neighbour in the label row. */
.nx-pin-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--nxt-size-unit) * 1);
  height: calc(var(--nxt-size-unit) * 1);
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: none;
  color: var(--nxt-fg-muted);
  line-height: 1;
  cursor: pointer;
  transition:
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-pin-toggle.is-pinned {
  color: var(--nxt-brand-subtle-fg);
}
.nx-pin-toggle:hover:not(:disabled) {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-pin-toggle:disabled {
  cursor: default;
  opacity: 0.6;
}
.nx-pin-toggle__icon {
  width: 100%;
  height: 100%;
}
@media (prefers-reduced-motion: reduce) {
  .nx-pin-toggle {
    transition: none;
  }
}
</style>
